import { SvgIcon } from '@progress/kendo-react-common';
import { type ReactNode, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import CustomTitleBar from 'src/components/GuideMeTitleBar';
import Button from 'src/components/design-system/Button';
import { CanFlagCompanySpecified } from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import { useContextSelector } from 'use-context-selector';
import { ShoppingListProductsContext } from './ShoppingListProductsContext';
import { shoppingListDragIndicatorIcon } from './shoppingListReorder';

/** Ignores Trans-injected children so the icon always renders. */
const GuideDragHandleIcon = (_props: { children?: ReactNode }) => (
  <span className="mx-1 inline-flex align-middle text-neutral-500">
    <SvgIcon icon={shoppingListDragIndicatorIcon} />
  </span>
);

const guideCompanySpecifiedButton = (
  <Button color="secondary" className="inline-block" readonly size="md">
    {null}
  </Button>
);

const companySpecifiedGuideTransComponents = {
  companySpecified: guideCompanySpecifiedButton,
  markAction: <span className="font-bold" />,
  unmarkAction: <span className="font-bold" />,
};

const CompanySpecifiedGuideItems = () => (
  <>
    <li className="list-disc font-semibold">
      <Trans
        i18nKey="shoppingListProductsGuide.companySpecified"
        components={companySpecifiedGuideTransComponents}
      />
    </li>
    <li className="list-disc font-semibold">
      <Trans
        i18nKey="shoppingListProductsGuide.companySpecifiedUnmark"
        components={companySpecifiedGuideTransComponents}
      />
    </li>
  </>
);

const useShoppingListProductsGuidePermissions = () => {
  const ownsList = useContextSelector(
    ShoppingListProductsContext,
    (context) => !(context.shoppingList?.isShare ?? false)
  );
  const showCompanySpecified = usePermission({ permissions: CanFlagCompanySpecified });

  return {
    ownsList,
    canShowCompanySpecified: ownsList && showCompanySpecified,
  };
};

const ListViewGuideContent = () => {
  const { t } = useTranslation();
  const { ownsList, canShowCompanySpecified } = useShoppingListProductsGuidePermissions();

  return (
    <ul className="space-y-1 px-4 font-bold text-[13px]">
      <li className="list-disc font-semibold">{t('shoppingListProductsGuide.listDisplaysProducts')}</li>
      {ownsList ? (
        <li className="list-disc font-semibold">
          <Trans
            i18nKey="shoppingListProductsGuide.dragReorder"
            components={{
              dragHandle: <GuideDragHandleIcon />,
            }}
          />
        </li>
      ) : null}
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="shoppingListProductsGuide.addToCart"
          components={{
            addToCart: (
              <Button color="success" className="inline-block" readonly size="md">
                {null}
              </Button>
            ),
          }}
        />
      </li>
      {ownsList ? (
        <li className="list-disc font-semibold">
          <Trans
            i18nKey="shoppingListProductsGuide.delete"
            components={{
              deleteButton: (
                <Button color="error" className="inline-block" readonly size="md">
                  {null}
                </Button>
              ),
            }}
          />
        </li>
      ) : null}
      <li className="list-disc font-semibold">
        {ownsList ? (
          <Trans
            i18nKey="shoppingListProductsGuide.moveOrCopy"
            components={{
              moveTo: (
                <Button color="secondary" className="inline-block" readonly size="md">
                  {null}
                </Button>
              ),
              copyTo: (
                <Button color="secondary" className="inline-block" readonly size="md">
                  {null}
                </Button>
              ),
            }}
          />
        ) : (
          <Trans
            i18nKey="shoppingListProductsGuide.copyOnly"
            components={{
              copyTo: (
                <Button color="secondary" className="inline-block" readonly size="md">
                  {null}
                </Button>
              ),
            }}
          />
        )}
      </li>
      <li className="list-disc font-semibold">{t('shoppingListProductsGuide.filterBar')}</li>
      {canShowCompanySpecified ? <CompanySpecifiedGuideItems /> : null}
    </ul>
  );
};

const PictureViewGuideContent = () => {
  const { t } = useTranslation();
  const { ownsList, canShowCompanySpecified } = useShoppingListProductsGuidePermissions();

  return (
    <ul className="space-y-1 px-4 font-bold text-[13px]">
      <li className="list-disc font-semibold">{t('shoppingListProductsGuide.pictureDisplaysCards')}</li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="shoppingListProductsGuide.addToCart"
          components={{
            addToCart: (
              <Button color="success" className="inline-block" readonly size="md">
                {null}
              </Button>
            ),
          }}
        />
      </li>
      {ownsList ? (
        <li className="list-disc font-semibold">
          <Trans
            i18nKey="shoppingListProductsGuide.delete"
            components={{
              deleteButton: (
                <Button color="error" className="inline-block" readonly size="md">
                  {null}
                </Button>
              ),
            }}
          />
        </li>
      ) : null}
      <li className="list-disc font-semibold">
        {ownsList ? (
          <Trans
            i18nKey="shoppingListProductsGuide.moveOrCopy"
            components={{
              moveTo: (
                <Button color="secondary" className="inline-block" readonly size="md">
                  {null}
                </Button>
              ),
              copyTo: (
                <Button color="secondary" className="inline-block" readonly size="md">
                  {null}
                </Button>
              ),
            }}
          />
        ) : (
          <Trans
            i18nKey="shoppingListProductsGuide.copyOnly"
            components={{
              copyTo: (
                <Button color="secondary" className="inline-block" readonly size="md">
                  {null}
                </Button>
              ),
            }}
          />
        )}
      </li>
      <li className="list-disc font-semibold">{t('shoppingListProductsGuide.filterBar')}</li>
      {canShowCompanySpecified ? <CompanySpecifiedGuideItems /> : null}
    </ul>
  );
};

export const useShoppingListProductsGuideMeSteps = (selectedView: number) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        content: selectedView === 0 ? <ListViewGuideContent /> : <PictureViewGuideContent />,
        title: (
          <CustomTitleBar
            title={
              selectedView === 0
                ? t('shoppingListProductsGuide.listViewTitle')
                : t('shoppingListProductsGuide.pictureViewTitle')
            }
            className="font-bold text-xl text-red-600"
          />
        ),
      },
    ],
    [selectedView, t]
  );
};

export default useShoppingListProductsGuideMeSteps;
